import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { IoPersonOutline } from "react-icons/io5";
import "./accountTypeSelect.css";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const BASE_URL = REST_API_URL;

export default function AccountTypeSelect() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const token = useMemo(() => {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken")
    );
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/sign-in", { replace: true });
    }
  }, [token, navigate]);

  const handleNext = async () => {
    if (!selected) {
      toast.error(t("auth.errorSelectAccount"));
      return;
    }

    setLoading(true);

    try {
      const chosenStatus = selected === "business" ? "salon" : "individual";

      const tokenNow =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!tokenNow) {
        toast.error(t("auth.errorNoToken"));
        return;
      }

      const res = await fetch(`${BASE_URL}/auth/update-status/`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({ status: chosenStatus }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.detail || t("auth.errorUpdateFailed"));
        return;
      }
      const forcedRoute =
        chosenStatus === "salon" ? "/biography-salon" : "/biography-individual";

      localStorage.setItem("user_status", chosenStatus);

      sessionStorage.setItem("onboarding_force_route", forcedRoute);
      sessionStorage.setItem("onboarding_force_status", chosenStatus);

      toast.success(t("auth.successAccountSaved"));
      navigate(forcedRoute, { replace: true });
    } catch (err) {
      console.error("UPDATE STATUS ERROR:", err);
      toast.error(t("auth.errorNetwork"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEnter = (e) => {
      if (e.key === "Enter" && selected && !loading) {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, [selected, loading]);

  const options = [
    {
      id: "individual",
      icon: <IoPersonOutline />,
      title: t("auth.optionIndividualTitle"),
      desc: t("auth.optionIndividualDesc"),
    },
    {
      id: "business",
      icon: <HiOutlineBuildingOffice2 />,
      title: t("auth.optionBusinessTitle"),
      desc: t("auth.optionBusinessDesc"),
    },
  ];

  return (
    <motion.div
      className="account-type"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <main className="account-type-main">
        <motion.div
          className="account-type-card"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="flex flex-col gap-4 mx-auto text-center">
            <h2 className="account-type-title">{t("auth.accountTypeTitle")}</h2>

            <p className="account-type-subtitle">
              {t("auth.accountTypeSubtitle")}
            </p>
          </div>
          <div className="account-type-options">
            {options.map((opt) => (
              <motion.div
                key={opt.id}
                className={`account-option ${selected === opt.id ? "selected" : ""}`}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setSelected(opt.id)}
              >
                <div
                  className={`option-icon ${selected === opt.id ? "active" : ""}`}
                >
                  {opt.icon}
                </div>
                <h3>{opt.title}</h3>
                <p>{opt.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.button
            className="account-type-btn"
            disabled={!selected || loading}
            onClick={handleNext}
            whileHover={{ scale: selected ? 1.05 : 1 }}
            whileTap={{ scale: selected ? 0.95 : 1 }}
          >
            {loading ? t("auth.savingStatus") : t("auth.next")}
          </motion.button>
        </motion.div>
      </main>
    </motion.div>
  );
}
